import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Save, Plus, Trash2, History, RotateCcw, Users, Search, Sigma,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const REGIONS_ORDER = ["North", "West", "South", "Overall", "Other"];
const REGION_TONE = {
  North: "bg-sky-50 text-sky-700 border-sky-200",
  West: "bg-emerald-50 text-emerald-700 border-emerald-200",
  South: "bg-amber-50 text-amber-700 border-amber-200",
  Overall: "bg-violet-50 text-violet-700 border-violet-200",
  Other: "bg-slate-50 text-slate-700 border-slate-200",
};

const fmtCr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

function VersionsDialog({ open, onOpenChange, canRestore, onRestored }) {
  const { data, isLoading } = useQuery({
    queryKey: ["plan-versions"],
    queryFn: async () => (await api.get("/sales-mis/plan-allocation/versions")).data,
    enabled: open,
  });
  const [restoring, setRestoring] = useState("");
  const restore = async (id) => {
    setRestoring(id);
    try {
      await api.post(`/sales-mis/plan-allocation/versions/${id}/restore`);
      toast.success("Plan Allocation restored from this version.");
      onRestored();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRestoring("");
    }
  };
  const versions = data || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="plan-versions-dialog">
        <DialogHeader>
          <DialogTitle>Plan Allocation history</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            A checkpoint is saved every 4 hours of edits. Restore reverts the sheet (current state is auto-saved first).
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2">
          {isLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : versions.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-400">No versions yet.</p>
          ) : (
            versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`plan-version-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{new Date(v.saved_at).toLocaleString()}</p>
                  <p className="text-xs text-slate-400 truncate">{v.note} · by {v.saved_by}</p>
                </div>
                {canRestore && (
                  <Button size="sm" variant="outline" disabled={!!restoring} onClick={() => restore(v.id)} className="gap-1.5 shrink-0" data-testid={`plan-version-restore-${v.id}`}>
                    {restoring === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const PersonSlot = memo(function PersonSlot({ person, canEdit, onChange, users, dataTestPrefix }) {
  const isVacant = person.is_vacant || (person.name || "").toLowerCase() === "vacant";
  const tone = person.is_me
    ? "bg-brand/10 border-brand/30 text-slate-900 font-semibold"
    : isVacant
      ? "bg-slate-50 border-slate-200 text-slate-400 italic"
      : "bg-white border-slate-200 text-slate-700";
  return (
    <div className={`flex items-center gap-1 rounded-md border px-1.5 py-1 ${tone}`} data-testid={dataTestPrefix}>
      {canEdit ? (
        <>
          <Input
            value={person.name || ""}
            onChange={(e) => onChange({ ...person, name: e.target.value })}
            className="h-6 w-20 px-1.5 text-[11px] border-0 shadow-none focus-visible:ring-0 bg-transparent"
            placeholder="Name / Vacant"
            data-testid={`${dataTestPrefix}-name`}
          />
          <Input
            type="number"
            step="0.1"
            value={person.amount ?? ""}
            onChange={(e) => onChange({ ...person, amount: e.target.value === "" ? null : Number(e.target.value) })}
            className="h-6 w-14 px-1.5 text-[11px] font-mono text-right border-0 shadow-none focus-visible:ring-0 bg-transparent"
            placeholder="0"
            data-testid={`${dataTestPrefix}-amt`}
          />
          {users.length > 0 && (
            <Select
              value={person.user_id || "__none__"}
              onValueChange={(v) => onChange({ ...person, user_id: v === "__none__" ? null : v })}
            >
              <SelectTrigger className="h-6 w-6 p-0 border-0 shadow-none focus:ring-0 bg-transparent [&>svg]:hidden" data-testid={`${dataTestPrefix}-map`} title="Map to CRM user">
                <Users className={`h-3 w-3 ${person.user_id ? "text-brand" : "text-slate-300 hover:text-slate-500"}`} />
              </SelectTrigger>
              <SelectContent align="end" className="max-h-72">
                <SelectItem value="__none__">— Unmapped —</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.role?.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </>
      ) : (
        <>
          <span className="text-[11px] truncate max-w-[80px]">{person.name || "—"}</span>
          <span className="text-[11px] font-mono ml-auto">{fmtCr(person.amount)}</span>
          {person.is_me && <Badge className="h-4 px-1 text-[9px] bg-brand text-white ml-1">You</Badge>}
        </>
      )}
    </div>
  );
});

const PlanRow = memo(function PlanRow({ row, ri, canEdit, users, onUpdate, onDelete }) {
  const rollupCls = row.rollup ? "bg-slate-50 font-semibold" : "";
  const mineCls = row.is_mine ? "bg-brand/[.04]" : "";
  return (
    <tr className={`border-b border-slate-100 group ${rollupCls} ${mineCls}`} data-testid={`plan-row-${ri}`}>
      <td className="px-2 py-1.5">
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${REGION_TONE[row.region] || REGION_TONE.Other}`}>
          {row.region || "Other"}
        </Badge>
      </td>
      <td className="px-2 py-1.5 min-w-[160px]">
        {canEdit ? (
          <div className="flex items-center gap-1">
            <Input
              value={row.segment || ""}
              onChange={(e) => onUpdate({ ...row, segment: e.target.value })}
              className="h-7 w-36 px-2 text-xs font-medium"
              placeholder="Row label…"
              data-testid={`plan-row-segment-${ri}`}
            />
          </div>
        ) : (
          <span className="text-xs font-medium text-slate-800">{row.segment}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canEdit ? (
          <Input
            type="number"
            step="0.1"
            value={row.total ?? ""}
            onChange={(e) => onUpdate({ ...row, total: e.target.value === "" ? null : Number(e.target.value) })}
            className="h-7 w-20 px-1.5 text-xs font-mono text-right"
            placeholder="0"
            data-testid={`plan-row-total-${ri}`}
          />
        ) : (
          <span className="text-xs font-mono font-semibold">{fmtCr(row.total)}</span>
        )}
      </td>
      {row.people.map((p, pi) => (
        <td key={pi} className="px-1 py-1.5">
          <PersonSlot
            person={p}
            canEdit={canEdit}
            users={users}
            onChange={(next) => {
              const people = [...row.people];
              people[pi] = { ...next };
              onUpdate({ ...row, people });
            }}
            dataTestPrefix={`plan-row-${ri}-person-${pi}`}
          />
        </td>
      ))}
      {canEdit && (
        <td className="px-1 py-1.5 text-right">
          <button
            onClick={() => onDelete(ri)}
            className="h-6 w-6 rounded flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`plan-row-delete-${ri}`}
            title="Delete row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </td>
      )}
      {!canEdit && <td className="px-1" />}
    </tr>
  );
});

export function PlanAllocation() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["plan-allocation"],
    queryFn: async () => (await api.get("/sales-mis/plan-allocation")).data,
  });
  const canEdit = !!data?.can_edit;
  const canRestore = !!data?.can_restore;
  const maxPeople = data?.max_people || 3;
  const users = useMemo(() => data?.people_users || [], [data?.people_users]);

  const [title, setTitle] = useState("");
  const [asOf, setAsOf] = useState("");
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (data?.sheet) {
      setTitle(data.sheet.title || "");
      setAsOf(data.sheet.as_of || "");
      setRows((data.sheet.rows || []).map((r) => ({ ...r, people: r.people.map((p) => ({ ...p })) })));
      setDirty(false);
    }
  }, [data?.sheet]);

  const updateRow = useCallback((ri, next) => {
    setRows((prev) => {
      const arr = [...prev];
      arr[ri] = next;
      return arr;
    });
    setDirty(true);
  }, []);

  const deleteRow = useCallback((ri) => {
    setRows((prev) => prev.filter((_, i) => i !== ri));
    setDirty(true);
  }, []);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        segment: "",
        region: "Other",
        total: null,
        rollup: false,
        people: Array.from({ length: maxPeople }, () => ({ name: "", amount: null, user_id: null })),
      },
    ]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title,
        as_of: asOf,
        rows: rows.map((r) => ({
          id: r.id,
          segment: r.segment,
          region: r.region,
          total: r.total,
          rollup: !!r.rollup,
          people: r.people.map((p) => ({ name: p.name || "", amount: p.amount ?? null, user_id: p.user_id || null })),
        })),
      };
      const { data: fresh } = await api.put("/sales-mis/plan-allocation", payload);
      setRows((fresh.sheet.rows || []).map((r) => ({ ...r, people: r.people.map((p) => ({ ...p })) })));
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["plan-allocation"] });
      qc.invalidateQueries({ queryKey: ["plan-versions"] });
      toast.success("Plan Allocation saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (mineOnly) out = out.filter((r) => r.is_mine);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) =>
        (r.segment || "").toLowerCase().includes(needle) ||
        (r.region || "").toLowerCase().includes(needle) ||
        (r.people || []).some((p) => (p.name || "").toLowerCase().includes(needle))
      );
    }
    return out;
  }, [rows, mineOnly, q]);

  // Group by region for the category-wise banded display
  const grouped = useMemo(() => {
    const buckets = {};
    filtered.forEach((r) => {
      const reg = r.region || "Other";
      (buckets[reg] ||= []).push(r);
    });
    const ordered = REGIONS_ORDER.filter((k) => buckets[k]).map((k) => ({ region: k, rows: buckets[k] }));
    // include any regions not in REGIONS_ORDER at the end
    Object.keys(buckets).forEach((k) => {
      if (!REGIONS_ORDER.includes(k)) ordered.push({ region: k, rows: buckets[k] });
    });
    return ordered;
  }, [filtered]);

  const totals = useMemo(() => {
    const allocated = rows.reduce((sum, r) => sum + (r.rollup ? 0 : (r.people || []).reduce((s, p) => s + (Number(p.amount) || 0), 0)), 0);
    const totalPlan = rows.reduce((sum, r) => sum + (r.rollup ? 0 : (Number(r.total) || 0)), 0);
    const mine = rows.reduce((sum, r) => sum + (r.people || []).reduce((s, p) => s + (p.is_me ? (Number(p.amount) || 0) : 0), 0), 0);
    return { allocated, totalPlan, mine };
  }, [rows]);

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="plan-allocation-board">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} className="h-9 max-w-xs font-medium" placeholder="Sheet title…" data-testid="plan-title-input" />
              <Input value={asOf} onChange={(e) => { setAsOf(e.target.value); setDirty(true); }} className="h-9 max-w-[180px] text-xs" placeholder="As-of date…" data-testid="plan-asof-input" />
            </div>
          ) : (
            <div>
              <p className="text-sm font-semibold text-slate-800">{title || "Team Plan Allocation"}</p>
              {asOf && <p className="text-xs text-slate-400">As of {asOf}</p>}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-1">
            Per-person share of every branch&apos;s Sales Plan · INR Crore ·{" "}
            {data?.sheet?.updated_at ? `last edited ${new Date(data.sheet.updated_at).toLocaleString()}${data.sheet.updated_by ? ` by ${data.sheet.updated_by}` : ""}` : "shared workbook"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canRestore && (
            <Button variant="outline" onClick={() => setShowVersions(true)} className="gap-1.5" data-testid="plan-history-btn">
              <History className="h-4 w-4" /> History
            </Button>
          )}
          {canEdit && (
            <>
              <Button variant="outline" onClick={addRow} className="gap-1.5" data-testid="plan-add-row-btn">
                <Plus className="h-4 w-4" /> Add row
              </Button>
              <Button onClick={save} disabled={!dirty || saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="plan-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Total Sales Plan</p>
          <p className="font-heading text-xl font-bold text-slate-900">{fmtCr(totals.totalPlan)}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Allocated to People</p>
          <p className="font-heading text-xl font-bold text-slate-900">{fmtCr(totals.allocated)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {totals.totalPlan ? `${((totals.allocated / totals.totalPlan) * 100).toFixed(0)}% of plan covered` : ""}
          </p>
        </Card>
        <Card className="p-3 border-brand/40 bg-brand/[.04]">
          <p className="text-[11px] uppercase tracking-wide text-brand">Your Share</p>
          <p className="font-heading text-xl font-bold text-slate-900">{fmtCr(totals.mine) || "—"}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Sum of allocations mapped to your name.</p>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64 pl-8 text-xs" placeholder="Search segment or person…" data-testid="plan-search-input" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
          <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="plan-mine-toggle" />
          Show only my rows
        </label>
      </div>

      {grouped.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="plan-empty">
          {mineOnly ? "No allocations are mapped to you yet. Your Sales Head can map you from the person dropdown." : "No rows yet."}
        </Card>
      ) : (
        grouped.map((band) => (
          <Card key={band.region} className="overflow-hidden mb-4">
            <div className={`px-3 py-2 border-b flex items-center gap-2 ${REGION_TONE[band.region] || REGION_TONE.Other}`}>
              <Sigma className="h-3.5 w-3.5" />
              <h3 className="text-[11px] uppercase tracking-wider font-semibold">{band.region}</h3>
              <span className="text-[10px] opacity-70 ml-auto">{band.rows.length} row{band.rows.length === 1 ? "" : "s"}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[80px]">Region</th>
                    <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Segment</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Plan (Cr)</th>
                    {Array.from({ length: maxPeople }).map((_, i) => (
                      <th key={i} className="text-left px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Person {i + 1}</th>
                    ))}
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {band.rows.map((row) => {
                    const globalIdx = rows.findIndex((x) => x.id === row.id);
                    return (
                      <PlanRow
                        key={row.id}
                        row={row}
                        ri={globalIdx}
                        canEdit={canEdit}
                        users={users}
                        onUpdate={(next) => updateRow(globalIdx, next)}
                        onDelete={deleteRow}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}

      <VersionsDialog
        open={showVersions}
        onOpenChange={setShowVersions}
        canRestore={canRestore}
        onRestored={() => { qc.invalidateQueries({ queryKey: ["plan-allocation"] }); qc.invalidateQueries({ queryKey: ["plan-versions"] }); }}
      />
    </div>
  );
}
